import { supabase } from './supabase';

const SIGNED_URL_EXPIRY_SECONDS = 3600;
const CACHE_BUFFER_SECONDS = 300;

interface CachedUrl {
  signedUrl: string;
  expiresAt: number;
}

const urlCache = new Map<string, CachedUrl>();

export function extractStoragePath(url: string, bucket: string): string | null {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const pathMatch = urlObj.pathname.match(new RegExp(`/storage/v1/object/(?:public|sign)/${bucket}/(.+)`));
    if (pathMatch) {
      return decodeURIComponent(pathMatch[1].split('?')[0]);
    }

    const publicMatch = urlObj.pathname.match(new RegExp(`/storage/v1/object/public/${bucket}/(.+)`));
    if (publicMatch) {
      return decodeURIComponent(publicMatch[1]);
    }

    if (urlObj.pathname.includes(bucket)) {
      const bucketIndex = urlObj.pathname.indexOf(bucket);
      const pathAfterBucket = urlObj.pathname.substring(bucketIndex + bucket.length + 1);
      if (pathAfterBucket) {
        return decodeURIComponent(pathAfterBucket.split('?')[0]);
      }
    }
  } catch {
    const segments = url.split('/');
    const bucketIndex = segments.findIndex(s => s === bucket);
    if (bucketIndex !== -1 && bucketIndex < segments.length - 1) {
      return segments.slice(bucketIndex + 1).join('/').split('?')[0];
    }
  }

  return null;
}

function getCacheKey(bucket: string, path: string): string {
  return `${bucket}:${path}`;
}

function getCachedUrl(bucket: string, path: string): string | null {
  const key = getCacheKey(bucket, path);
  const cached = urlCache.get(key);

  if (!cached) return null;

  const now = Date.now();
  if (now >= cached.expiresAt - CACHE_BUFFER_SECONDS * 1000) {
    urlCache.delete(key);
    return null;
  }

  return cached.signedUrl;
}

function setCachedUrl(bucket: string, path: string, signedUrl: string): void {
  const key = getCacheKey(bucket, path);
  const expiresAt = Date.now() + SIGNED_URL_EXPIRY_SECONDS * 1000;
  urlCache.set(key, { signedUrl, expiresAt });
}

export async function getSignedUrl(
  originalUrl: string | null | undefined,
  bucket: 'photos' | 'recordings' | 'images'
): Promise<string | null> {
  if (!originalUrl) return null;

  const path = extractStoragePath(originalUrl, bucket);
  if (!path) {
    console.warn(`[SignedUrls] Could not extract path from URL: ${originalUrl}`);
    return originalUrl;
  }

  const cached = getCachedUrl(bucket, path);
  if (cached) {
    return cached;
  }

  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, SIGNED_URL_EXPIRY_SECONDS);

    if (error) {
      console.error(`[SignedUrls] Error creating signed URL for ${bucket}/${path}:`, error);
      return originalUrl;
    }

    if (data?.signedUrl) {
      setCachedUrl(bucket, path, data.signedUrl);
      return data.signedUrl;
    }

    return originalUrl;
  } catch (err) {
    console.error(`[SignedUrls] Exception creating signed URL:`, err);
    return originalUrl;
  }
}

export async function getSignedUrls(
  items: Array<{ url: string | null | undefined; bucket: 'photos' | 'recordings' | 'images' }>
): Promise<Map<string, string>> {
  const results = new Map<string, string>();
  const toFetch: Array<{ originalUrl: string; bucket: string; path: string }> = [];

  for (const { url, bucket } of items) {
    if (!url) continue;

    const path = extractStoragePath(url, bucket);
    if (!path) {
      results.set(url, url);
      continue;
    }

    const cached = getCachedUrl(bucket, path);
    if (cached) {
      results.set(url, cached);
    } else {
      toFetch.push({ originalUrl: url, bucket, path });
    }
  }

  const byBucket = new Map<string, Array<{ originalUrl: string; path: string }>>();
  for (const item of toFetch) {
    const list = byBucket.get(item.bucket) || [];
    list.push({ originalUrl: item.originalUrl, path: item.path });
    byBucket.set(item.bucket, list);
  }

  const fetchPromises: Promise<void>[] = [];

  for (const [bucket, paths] of byBucket) {
    const promise = (async () => {
      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrls(
            paths.map(p => p.path),
            SIGNED_URL_EXPIRY_SECONDS
          );

        if (error) {
          console.error(`[SignedUrls] Batch error for ${bucket}:`, error);
          paths.forEach(p => results.set(p.originalUrl, p.originalUrl));
          return;
        }

        if (data) {
          data.forEach((result, index) => {
            const item = paths[index];
            if (result.signedUrl) {
              setCachedUrl(bucket as any, item.path, result.signedUrl);
              results.set(item.originalUrl, result.signedUrl);
            } else {
              results.set(item.originalUrl, item.originalUrl);
            }
          });
        }
      } catch (err) {
        console.error(`[SignedUrls] Batch exception for ${bucket}:`, err);
        paths.forEach(p => results.set(p.originalUrl, p.originalUrl));
      }
    })();

    fetchPromises.push(promise);
  }

  await Promise.all(fetchPromises);

  return results;
}

export function clearSignedUrlCache(): void {
  urlCache.clear();
}

export function getStoragePath(
  originalUrl: string | null | undefined,
  bucket: 'photos' | 'recordings' | 'images'
): string | null {
  if (!originalUrl) return null;
  return extractStoragePath(originalUrl, bucket);
}
